// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI (Quest review E1, E2, E4, B7, 2026-11-03): where a placed globe is
 * held, frame by frame. Moved out of `vrSession.ts` so it can be tested
 * against fake XR frames (`vrGlobePlacement.test.ts`).
 *
 * A placement is a surface point and a hold (`vrGlobeHold.ts`):
 *   - The point is anchored where the device can: a WebXR anchor stays
 *     bolted to the real surface when the device refines its map or
 *     re-bases its floor. The anchor is made on the next XR frame, because
 *     `createAnchor` needs a frame and a tap on a phone's Place is a DOM
 *     event outside any (E1: a hand-placed globe used to get no anchor).
 *   - The hold gives the centre's height above the point for the globe's
 *     size every frame, so a resting globe keeps its bottom on the surface
 *     as it grows (E2).
 *   - In a room aligned by the shared marker, the anchor carries the
 *     marker's heading, and the heading follows the anchor's orientation
 *     as the map is refined, turning the globe with it (E4).
 *   - A placement by hand is saved for the next session where the device
 *     offers persistent anchors (Meta Quest), with its hold (B7). A
 *     restored globe found behind the viewer or across the room is let
 *     go, for the session to bring it in front of them.
 */

import type * as THREE from 'three'
import { encodeSavedPlacement, holdOffset, isOutOfReach, type GlobeHold } from './vrGlobeHold'
import { logger } from '../utils/logger'

export interface GlobePlacementDeps {
  /** The globe the placement moves. */
  readonly globe: THREE.Object3D
  /** An AR session: there are real surfaces to anchor to. */
  readonly isAr: boolean
  /** The globe's radius now, metres. */
  radius(): number
  /**
   * The room's heading while the session is aligned by the marker (the
   * marker frame's orientation, turned in place as the anchor refines
   * it), else null.
   */
  markerHeading(): THREE.Quaternion | null
  /** The session this belongs to is still running. */
  isLive(): boolean
  /** A placement by hand has been made: anchored and saved for the next session, or not. */
  onPlacedByHand?(persisted: boolean): void
  /** Keep a placement for the next session (`vrPersistence.ts`). */
  savePlacement(encoded: string): void
  /** Forget the saved placement. */
  clearSavedPlacement(): void
  /** A restored globe was out of reach and has been let go: bring it in front of the viewer. */
  onOutOfReach(): void
}

export interface GlobePlacementHandle {
  /**
   * Put the globe on a surface point with its hold, and anchor the point
   * on the next frame.
   *
   * @param heading the marker's frame to anchor with, so the anchor
   *   carries the room's heading, or null.
   * @param byHand a placement by hand: saved for the next session where
   *   the device allows, and reported through `onPlacedByHand`.
   */
  place(
    surface: { readonly x: number; readonly y: number; readonly z: number },
    hold: GlobeHold,
    heading: THREE.Quaternion | null,
    byHand: boolean,
  ): void
  /** A placement restored from the last session's anchor. Its first pose is checked against the viewer. */
  restore(anchor: XRAnchor, hold: GlobeHold): void
  /** Let go: no hold, no anchor, nothing saved (a globe carried by hand). */
  release(): void
  /** Once per frame, outside Place mode: make a pending anchor, and hold the globe. */
  update(frame: XRFrame | null | undefined, refSpace: XRReferenceSpace | null): void
  /** The centre's height above the surface point now, metres (0 while nothing is placed). */
  offset(): number
  /** Something is placed. */
  isPlaced(): boolean
  /** The anchor, for checks. */
  anchor(): XRAnchor | null
  /** The session is ending: drop the anchor, keep what is saved for the next one. */
  dispose(): void
}

interface PendingAnchor {
  readonly position: THREE.Vector3
  readonly orientation: THREE.Quaternion
  readonly byHand: boolean
}

export function createGlobePlacement(THREE_: typeof THREE, deps: GlobePlacementDeps): GlobePlacementHandle {
  let hold: GlobeHold | null = null
  /** The surface's height at the placement, for the frames without an anchor pose. */
  let surfaceY: number | null = null
  let currentAnchor: XRAnchor | null = null
  /** The anchor carries the marker's heading. */
  let anchorHeading = false
  let pending: PendingAnchor | null = null
  /** Bumped on every placement and release, so a slow anchor from an older one is thrown away. */
  let token = 0
  let restoreCheck = false
  let offsetNow = 0

  const anchorTurn = new THREE_.Quaternion()
  const headingDelta = new THREE_.Quaternion()
  const viewerTurn = new THREE_.Quaternion()
  const forward = new THREE_.Vector3()

  function dropAnchor(): void {
    if (currentAnchor) {
      try { currentAnchor.delete() } catch { /* already gone */ }
      currentAnchor = null
    }
    pending = null
    anchorHeading = false
  }

  function makeAnchor(frame: XRFrame, refSpace: XRReferenceSpace, want: PendingAnchor): void {
    const mine = token
    const heldAs = hold
    const createFn = frame.createAnchor
    if (!createFn) {
      if (want.byHand) deps.onPlacedByHand?.(false)
      return
    }
    const p = want.position
    const q = want.orientation
    void createFn
      .call(frame, new XRRigidTransform({ x: p.x, y: p.y, z: p.z }, { x: q.x, y: q.y, z: q.z, w: q.w }), refSpace)
      .then(async (anchor) => {
        // A newer placement, a release or the session's end since: not wanted.
        if (!deps.isLive() || mine !== token) {
          try { anchor.delete() } catch { /* already gone */ }
          return
        }
        currentAnchor = anchor
        // Persistent handle for the next session. Meta Quest offers it;
        // other browsers may not. The anchor still tracks in this session.
        let persisted = false
        const requestFn = want.byHand ? anchor.requestPersistentHandle : undefined
        if (requestFn && heldAs) {
          try {
            const handle = await requestFn.call(anchor)
            deps.savePlacement(encodeSavedPlacement({ handle, hold: heldAs }))
            persisted = true
            logger.info('[VR] Saved persistent placement anchor')
          } catch (err) {
            logger.debug('[VR] Anchor persistent handle not available:', err)
          }
        }
        if (want.byHand) deps.onPlacedByHand?.(persisted)
      })
      .catch((err) => {
        logger.warn('[VR] Failed to anchor the placement:', err)
        if (want.byHand) deps.onPlacedByHand?.(false)
      })
  }

  function release(): void {
    if (!hold && !currentAnchor && !pending) return
    token++
    dropAnchor()
    hold = null
    surfaceY = null
    restoreCheck = false
    offsetNow = 0
    deps.clearSavedPlacement()
  }

  return {
    place(surface, nextHold, heading, byHand) {
      dropAnchor()
      token++
      hold = nextHold
      surfaceY = surface.y
      restoreCheck = false
      offsetNow = holdOffset(nextHold, deps.radius())
      deps.globe.position.set(surface.x, surface.y + offsetNow, surface.z)
      anchorHeading = heading !== null
      if (deps.isAr) {
        pending = {
          position: new THREE_.Vector3(surface.x, surface.y, surface.z),
          orientation: heading ? heading.clone() : new THREE_.Quaternion(),
          byHand,
        }
      } else if (byHand) {
        // VR has no real surface to anchor to: the placed spot stands as it is.
        deps.onPlacedByHand?.(false)
      }
    },

    restore(anchor, savedHold) {
      dropAnchor()
      token++
      currentAnchor = anchor
      hold = savedHold
      surfaceY = null
      restoreCheck = true
    },

    release,

    update(frame, refSpace) {
      if (pending && frame && refSpace) {
        const want = pending
        pending = null
        makeAnchor(frame, refSpace, want)
      }
      if (!hold) return
      offsetNow = holdOffset(hold, deps.radius())
      const pose = currentAnchor && frame && refSpace ? frame.getPose(currentAnchor.anchorSpace, refSpace) : null
      if (!pose) {
        if (surfaceY !== null) deps.globe.position.y = surfaceY + offsetNow
        return
      }
      const ap = pose.transform.position
      surfaceY = ap.y
      deps.globe.position.set(ap.x, ap.y + offsetNow, ap.z)
      // The marker's heading follows the anchor's as the map is refined,
      // and the globe turns with it, so phones that scanned the same
      // marker stay agreed through a lesson.
      const heading = anchorHeading ? deps.markerHeading() : null
      if (heading) {
        const o = pose.transform.orientation
        anchorTurn.set(o.x, o.y, o.z, o.w)
        if (Math.abs(anchorTurn.dot(heading)) < 1 - 1e-9) {
          // The turn from the old heading to the new: new × old⁻¹.
          headingDelta.copy(heading).invert().premultiply(anchorTurn)
          deps.globe.quaternion.premultiply(headingDelta)
          heading.copy(anchorTurn)
        }
      }
      // A restored globe behind the viewer or across the room is let go;
      // judged from this frame's own viewer pose.
      const viewer = restoreCheck && refSpace ? frame?.getViewerPose(refSpace) : null
      if (viewer) {
        restoreCheck = false
        const vq = viewer.transform.orientation
        forward.set(0, 0, -1).applyQuaternion(viewerTurn.set(vq.x, vq.y, vq.z, vq.w))
        if (isOutOfReach(viewer.transform.position, forward, deps.globe.position)) {
          release()
          deps.onOutOfReach()
        }
      }
    },

    offset() {
      return offsetNow
    },

    isPlaced() {
      return hold !== null
    },

    anchor() {
      return currentAnchor
    },

    dispose() {
      token++
      dropAnchor()
      hold = null
      surfaceY = null
      restoreCheck = false
    },
  }
}
